import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, ModoContext, SoloLecturaContext, useEnergia } from "../engine";
import { GuideScreen } from "../GuideScreen/GuideScreen";
import { InstallationProfileForm } from "../InstallationProfileForm/InstallationProfileForm";
import { SustemiaConnection } from "../SustemiaConnection/SustemiaConnection";
import { DocumentChecklist } from "../DocumentChecklist/DocumentChecklist";
import { EnergyReviewUSE } from "../EnergyReviewUSE/EnergyReviewUSE";
import { MachineryInventory } from "../MachineryInventory/MachineryInventory";
import { TheoreticalEstimation } from "../TheoreticalEstimation/TheoreticalEstimation";
import { BaselineScreen } from "../BaselineScreen/BaselineScreen";
import { EnpiScreen } from "../EnpiScreen/EnpiScreen";
import { ProcessPerformance } from "../ProcessPerformance/ProcessPerformance";
import { IndexAndVerdict } from "../IndexAndVerdict/IndexAndVerdict";
import { EnergyPerformanceDashboard } from "../EnergyPerformanceDashboard/EnergyPerformanceDashboard";
import { SelfAssessmentForm } from "../SelfAssessmentForm/SelfAssessmentForm";
import { AutomaticDiagnosis } from "../AutomaticDiagnosis/AutomaticDiagnosis";
import { MethodologyGuide } from "../MethodologyGuide/MethodologyGuide";
import { DenimCycleView } from "../DenimCycleView/DenimCycleView";
import { MeasurementGuideView } from "../MeasurementGuideView/MeasurementGuideView";
import { AuditorGuide } from "../AuditorGuide/AuditorGuide";
import { OnSiteWorkPlan } from "../OnSiteWorkPlan/OnSiteWorkPlan";
import { FieldSurveys } from "../FieldSurveys/FieldSurveys";
import { EcmPortfolio } from "../EcmPortfolio/EcmPortfolio";
import { Recommendations } from "../Recommendations/Recommendations";
import { GapPlan } from "../GapPlan/GapPlan";
import { InformeReport } from "../InformeReport/InformeReport";
import "./EnergyPerformanceWorkspace.scss";

// Menú de la hoja 02: tres grupos y 24 pestañas. Los identificadores son los del prototipo (data-tab), así el
// recorrido de cada uso (hoja 16, RECORRIDO del motor) se aplica tal cual. sinInstalacion: se puede abrir antes
// de crear la instalación.
const GRUPOS = [
  {
    id: "1", labelKey: "energy_performance_group_1", pestanas: [
      { tab: "guia", labelKey: "energy_performance_module_guide", Component: GuideScreen, sinInstalacion: true },
      { tab: "ficha", labelKey: "energy_performance_module_profile", Component: InstallationProfileForm, sinInstalacion: true },
      { tab: "sustemia", labelKey: "energy_performance_module_sustemia", Component: SustemiaConnection, sinInstalacion: true },
      { tab: "documentacion", labelKey: "energy_performance_module_documents", Component: DocumentChecklist },
      { tab: "revision", labelKey: "energy_performance_module_review_use", Component: EnergyReviewUSE },
      { tab: "maquinaria", labelKey: "energy_performance_module_machinery", Component: MachineryInventory },
      { tab: "estimacion", labelKey: "energy_performance_module_estimation", Component: TheoreticalEstimation },
      { tab: "enb", labelKey: "energy_performance_module_iden_lben", Component: BaselineScreen },
      { tab: "iden", labelKey: "energy_performance_module_iden", Component: EnpiScreen },
      { tab: "operaciones", labelKey: "energy_performance_module_process_performance", Component: ProcessPerformance },
      { tab: "score", labelKey: "energy_performance_module_index_verdict", Component: IndexAndVerdict },
      { tab: "dashboard", labelKey: "energy_performance_module_dashboard", Component: EnergyPerformanceDashboard },
      { tab: "formulario", labelKey: "energy_performance_module_selfassessment", Component: SelfAssessmentForm },
      { tab: "auditoria", labelKey: "energy_performance_module_diagnosis", Component: AutomaticDiagnosis },
    ],
  },
  {
    id: "2", labelKey: "energy_performance_group_2", pestanas: [
      { tab: "metodologia", labelKey: "energy_performance_module_methodology", Component: MethodologyGuide, sinInstalacion: true },
      { tab: "denim", labelKey: "energy_performance_module_denim_cycle", Component: DenimCycleView, soloDenim: true },
      { tab: "mediciones", labelKey: "energy_performance_module_measurement_guide", Component: MeasurementGuideView },
      { tab: "auditorguia", labelKey: "energy_performance_module_auditor_guide", Component: AuditorGuide, sinInstalacion: true },
      { tab: "plantrabajo", labelKey: "energy_performance_module_worksite_plan", Component: OnSiteWorkPlan },
      { tab: "relevamientos", labelKey: "energy_performance_module_field_surveys", Component: FieldSurveys },
    ],
  },
  {
    id: "3", labelKey: "energy_performance_group_3", pestanas: [
      { tab: "ecm", labelKey: "energy_performance_module_ecm", Component: EcmPortfolio },
      { tab: "recomendaciones", labelKey: "energy_performance_module_recommendations", Component: Recommendations },
      { tab: "gaps", labelKey: "energy_performance_module_gap_plan", Component: GapPlan },
      { tab: "informe", labelKey: "energy_performance_module_informe", Component: InformeReport },
    ],
  },
];
const PESTANAS = GRUPOS.flatMap((g) => g.pestanas.map((p) => ({ ...p, grupo: g.id })));
const CLAVE_MODO = "sus_energia_modo"; // misma clave que el prototipo: preferencia del usuario, no de la planta

function leerModo() {
  try { const m = window.localStorage.getItem(CLAVE_MODO); return m === "A" || m === "B" ? m : "A"; } catch (e) { return "A"; }
}
function guardarModo(m) {
  try { window.localStorage.setItem(CLAVE_MODO, m); } catch (e) { /* sin almacenamiento: queda para esta sesión */ }
}

// Barra «Paso N de M» al pie de cada pantalla (hoja 01, fila 61).
function PasoBar({ tab, recorrido, modo, irA, t, nombre }) {
  const i = recorrido.findIndex(([x]) => x === tab);
  const uso = modo === "A" ? t("energy_performance_use_a") : t("energy_performance_use_b");
  if (i < 0) {
    return (
      <div className="ep-pasobar">
        <div className="ep-pasobar__text">{t("energy_performance_step_consult").replace("{uso}", uso)}</div>
        {recorrido.length > 0 && <Button type="button" basic size="small" onClick={() => irA(recorrido[0][0])}>{t("energy_performance_step_go_first")}: {nombre(recorrido[0][0])}</Button>}
      </div>
    );
  }
  const ant = recorrido[i - 1];
  const sig = recorrido[i + 1];
  return (
    <div className="ep-pasobar">
      <div className="ep-pasobar__text">
        <strong>{t("energy_performance_step_of").replace("{uso}", uso).replace("{n}", i + 1).replace("{m}", recorrido.length)}</strong> {recorrido[i][1]}.
        <div className="ep-section__note">{sig ? `${t("energy_performance_step_next")}: ${sig[1]}.` : t("energy_performance_step_last")}</div>
      </div>
      <div className="ep-pasobar__buttons">
        {ant && <Button type="button" basic size="small" onClick={() => irA(ant[0])}>← {t("energy_performance_step_prev")}</Button>}
        {sig && <Button type="button" primary size="small" onClick={() => irA(sig[0])}>{t("energy_performance_step_next")}: {nombre(sig[0])} →</Button>}
      </div>
    </div>
  );
}

// puedeEditar: permiso del usuario para modificar (por ahora, solo el administrador; hoja 23 p. 24-26 y 66).
export function EnergyPerformanceWorkspace({ installation, siteSelected, onInstallationSaved, puedeEditar = true }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [modo, setModo] = useState(leerModo);
  const [tab, setTab] = useState(installation ? "guia" : "ficha");
  const [abiertos, setAbiertos] = useState({ r: true });
  const [menuAbierto, setMenuAbierto] = useState(false);
  const panel = useRef(null);
  const enfocar = useRef(false);
  const vista = useEnergia(installation, { modo });
  const f = vista.f;

  const visible = (p) => !(p.soloDenim && !(f && P.esDenim(f)));
  const habilitada = (p) => !!installation || !!p.sinInstalacion;
  const nombre = (x) => { const p = PESTANAS.find((q) => q.tab === x); return p ? t(p.labelKey) : x; };
  // recorrido del uso elegido, sin las pestañas ocultas (p. ej. denim en una planta sin denim)
  const recorrido = useMemo(
    () => P.RECORRIDO[modo].filter(([x]) => { const p = PESTANAS.find((q) => q.tab === x); return p && visible(p); }),
    [modo, f], // eslint-disable-line
  );

  const irA = (x, { foco = true } = {}) => {
    const p = PESTANAS.find((q) => q.tab === x);
    if (!p || !habilitada(p) || !visible(p)) return;
    setTab(x);
    setAbiertos((a) => ({ ...a, [p.grupo]: true }));
    setMenuAbierto(false);
    enfocar.current = foco;
  };
  // «Siguiente» lleva al título de la pantalla y le da el foco (hoja 01, fila 130)
  useEffect(() => {
    if (!enfocar.current || !panel.current) return;
    enfocar.current = false;
    const h = panel.current.querySelector("h3");
    if (h) { h.setAttribute("tabindex", "-1"); h.focus({ preventScroll: true }); }
    if (panel.current.scrollIntoView) panel.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [tab]);
  // una pestaña que deja de existir (denim al cambiar el sector) vuelve a la guía
  useEffect(() => {
    const p = PESTANAS.find((q) => q.tab === tab);
    if (p && (!visible(p) || !habilitada(p))) setTab(installation ? "guia" : "ficha");
  }, [f, installation]); // eslint-disable-line

  const cambiarModo = (m) => { setModo(m); guardarModo(m); };
  const activa = PESTANAS.find((p) => p.tab === tab) || PESTANAS[0];
  const Componente = activa.Component;
  const caso = f && f._ejemplo ? P.casoEjemplo(f) : "";

  const botonPestana = (p, { numero, title } = {}) => (
    <button key={p.tab} type="button" className={`ep-ws__item${tab === p.tab ? " active" : ""}`} disabled={!habilitada(p)}
      aria-current={tab === p.tab ? "page" : undefined} title={title} onClick={() => irA(p.tab)}>
      {numero != null && <span className="ep-ws__num">{numero}</span>}
      <span>{t(p.labelKey)}</span>
    </button>
  );

  return (
    <ModoContext.Provider value={modo}>
      <SoloLecturaContext.Provider value={installation?.readOnly ? "ejemplo" : !puedeEditar ? "permiso" : false}>
      <div className={`ep-ws${menuAbierto ? " ep-ws--menu-abierto" : ""}`}>
        <aside className="ep-ws__sidebar" aria-label={t("energy_performance_menu")}>
          <Button type="button" size="small" className="ep-ws__menu-btn" aria-expanded={menuAbierto} aria-controls="ep-ws-menu" onClick={() => setMenuAbierto(!menuAbierto)}>
            <Icon name={menuAbierto ? "close" : "bars"} /> {menuAbierto ? t("energy_performance_menu_close") : t("energy_performance_menu")}
          </Button>
          <div id="ep-ws-menu" className="ep-ws__menu">
            <div className="ep-ws__modo">
              <div className="ep-ws__modo-label">{t("energy_performance_use_question")}</div>
              <div role="group" aria-label={t("energy_performance_use_question")} className="ep-ws__modo-sw">
                {["A", "B"].map((m) => (
                  <button key={m} type="button" aria-pressed={modo === m} className={modo === m ? "on" : ""} onClick={() => cambiarModo(m)}>
                    {m === "A" ? t("energy_performance_use_a") : t("energy_performance_use_b")}
                  </button>
                ))}
              </div>
            </div>

            <div className="ep-ws__group">
              <button type="button" className="ep-ws__toggle" aria-expanded={!!abiertos.r} onClick={() => setAbiertos((a) => ({ ...a, r: !a.r }))}>
                <span>{t("energy_performance_my_path")} <span className="ep-ws__count">{recorrido.length}</span></span><span className="ep-ws__chev">▾</span>
              </button>
              {abiertos.r && (
                <div className="ep-ws__items">
                  {recorrido.map(([x, desc], i) => botonPestana(PESTANAS.find((q) => q.tab === x), { numero: i + 1, title: desc }))}
                </div>
              )}
            </div>

            {GRUPOS.map((g) => {
              const items = g.pestanas.filter(visible);
              return (
                <div className="ep-ws__group" key={g.id}>
                  <button type="button" className="ep-ws__toggle" aria-expanded={!!abiertos[g.id]} onClick={() => setAbiertos((a) => ({ ...a, [g.id]: !a[g.id] }))}>
                    <span>{t(g.labelKey)} <span className="ep-ws__count">{items.length}</span></span><span className="ep-ws__chev">▾</span>
                  </button>
                  {/* un grupo cerrado no se renderiza: sus ítems quedan fuera del foco del teclado (fila 77) */}
                  {abiertos[g.id] && <div className="ep-ws__items">{items.map((p) => botonPestana(p))}</div>}
                </div>
              );
            })}
          </div>
        </aside>

        <main className="ep-ws__main">
          <div className="ep-ws__topbar">
            <span className="ep-ws__name">{installation?.name || t("energy_performance_new_installation")}</span>
            {installation && <span className="ep-ws__code">{installation.code}</span>}
            {installation && (
              <span className={`ep-ws__flag${f && f._ejemplo ? " ep-ws__flag--example" : ""}`}>
                {f && f._ejemplo ? t("energy_performance_flag_example") : t("energy_performance_flag_real")}
              </span>
            )}
          </div>
          {caso && <div className="ep-ws__caso" role="note"><strong>[EJEMPLO]</strong> {caso}</div>}

          <div className="ep-ws__panel" ref={panel}>
            {Componente === GuideScreen
              ? <GuideScreen onIr={(x) => irA(x)} />
              : <Componente installation={installation} siteSelected={siteSelected} onSaved={onInstallationSaved} />}
            <PasoBar tab={tab} recorrido={recorrido} modo={modo} irA={irA} t={t} nombre={nombre} />
          </div>
        </main>
      </div>
      </SoloLecturaContext.Provider>
    </ModoContext.Provider>
  );
}
